import { createBrowserRouter, RouterProvider, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { useEffect } from 'react';
import { Layout } from './components/Layout';
import { Workbench } from './pages/Workbench';
import { Dashboard } from './pages/Dashboard';
import { DemoLibrary } from './pages/DemoLibrary';
import { Login } from './pages/Login';
import { Landing } from './pages/Landing';
import { SsoCallback } from './pages/SsoCallback';
import { PrivacyPolicyPage, TermsOfUsePage } from './pages/LegalDocument';
import { AcceptLegal } from './pages/AcceptLegal';
import { RequireAuth } from './components/RequireAuth';
import { RequireFeature } from './components/RequireFeature';
import { GuestDemoLayout } from './components/GuestDemoLayout';
import { Entities } from './pages/Entities';
import { RiskRules } from './pages/RiskRules';
import { Cases } from './pages/Cases';
import { CaseDetail } from './pages/CaseDetail';
import { CaseMacro } from './pages/CaseMacro';
import { Settings } from './pages/Settings';
import { DataSources } from './pages/DataSources';
import { SearchHistory } from './pages/SearchHistory';
import P2PIntelligence from './pages/P2PIntelligence';
import { Transaction } from './pages/Transaction';
import { Analytics } from './pages/Analytics';
import { Feedback } from './pages/Feedback';
import { Documentation } from './pages/Documentation';
import { WatchlistAlerts } from './pages/WatchlistAlerts';
import TelegramIntelligence from './pages/TelegramIntelligence';
import InstagramOSINT from './pages/InstagramOSINT';
import InstagramNetwork from './pages/InstagramNetwork';
import FusionPage from './pages/FusionPage';
import CaseCanvasPage from './pages/CaseCanvasPage';
import FiatOffRamp from './pages/FiatOffRamp';
import HarvestFeed from './pages/HarvestFeed';
import SyndicateOperations from './pages/SyndicateOperations';
import { reportClientError } from './utils/reportClientError';
import { MaintenanceGate } from './components/MaintenanceGate';
import { WORKBENCH_SESSION_KEYS } from './data/workbenchDraft';


function isStorageQuotaError(message: string): boolean {
  return /exceeded the quota|QuotaExceededError/i.test(message);
}

function clearWorkbenchSessionAndReload(): void {
  try {
    for (const key of WORKBENCH_SESSION_KEYS) {
      sessionStorage.removeItem(key);
    }
  } catch {
    try { sessionStorage.clear(); } catch { /* ignore */ }
  }
  window.location.reload();
}

function RouteErrorFallback() {
  const error = useRouteError() as any;
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : (error?.message || 'Unexpected application error');
  const quotaError = !isRouteErrorResponse(error) && isStorageQuotaError(message);

  useEffect(() => {
    reportClientError(error instanceof Error ? error : new Error(message), {
      surface: 'route',
      endpoint: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [error, message]);

  return (
    <div className="min-h-screen bg-brand-page dark:bg-background text-foreground flex items-center justify-center p-6">
      <div className="w-full max-w-xl rounded-xl border border-border/60 bg-card shadow-lg overflow-hidden">
        <div className="h-1 w-full bg-primary" />
        <div className="p-6">
          <h1 className="text-lg font-semibold mb-2">Something went sideways</h1>
          <p className="text-sm text-muted-foreground mb-4">
            {quotaError
              ? 'This browser tab ran out of session storage while caching the investigation. Clearing the cache reloads the page. Unsaved work in this tab will be lost — reopen from Cases if you already saved a snapshot.'
              : 'We hit an unexpected error while loading this page.'}
          </p>
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive break-words">
            {message}
          </div>
          <div className="mt-5 flex gap-2">
            {quotaError ? (
              <button
                onClick={clearWorkbenchSessionAndReload}
                className="px-4 py-2 rounded-lg bg-primary hover:bg-brand-gold-hover text-primary-foreground text-sm font-medium transition-colors"
              >
                Clear cache and reload
              </button>
            ) : (
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg bg-primary hover:bg-brand-gold-hover text-primary-foreground text-sm font-medium transition-colors"
              >
                Reload
              </button>
            )}
            <button
              onClick={() => { window.location.href = '/'; }}
              className="px-4 py-2 rounded-lg border border-brand-ink bg-brand-ink text-white hover:bg-neutral-900 dark:bg-white/10 dark:text-foreground dark:border-border dark:hover:bg-white/15 text-sm font-medium transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Neutral placeholder shown while a lazy route resolves on first load (silences the HydrateFallback warning). */
export function HydrateFallbackElement() {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="hydrate-fallback"
      className="min-h-screen bg-brand-page dark:bg-background text-muted-foreground flex items-center justify-center text-sm"
    >
      Loading...
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/landing', element: <Landing />, errorElement: <RouteErrorFallback /> },
  { path: '/welcome', element: <Landing />, errorElement: <RouteErrorFallback /> },
  { path: '/login', element: <Login />, errorElement: <RouteErrorFallback /> },
  { path: '/privacy', element: <PrivacyPolicyPage />, errorElement: <RouteErrorFallback /> },
  { path: '/terms', element: <TermsOfUsePage />, errorElement: <RouteErrorFallback /> },
  {
    path: '/legal/accept',
    element: <RequireAuth requireLegal={false}><AcceptLegal /></RequireAuth>,
    errorElement: <RouteErrorFallback />,
  },
  // Shared case report: public by design. The URL token and the passcode (when set) are the credentials; the
  // page calls only /share/* and never the signed-in API (see pages/SharePage.tsx).
  { path: '/share/:token', lazy: async () => ({ Component: (await import('./pages/SharePage')).default }), errorElement: <RouteErrorFallback /> },
  // Partner SSO landing — public (no RequireAuth): it RECEIVES the session
  // token in the URL fragment and stores it before routing into the app.
  { path: '/sso-callback', element: <SsoCallback />, errorElement: <RouteErrorFallback /> },
  // Guest demo sandbox — Mock/Cached only; no RequireAuth (see GuestDemoLayout).
  {
    path: '/demo',
    element: <GuestDemoLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      { index: true, element: <DemoLibrary guest /> },
      { path: 'workbench', element: <Workbench /> },
      { path: 'fusion', element: <FusionPage /> },
      { path: 'documentation', element: <Documentation /> },
    ],
  },
  {
    path: '/',
    element: <RequireAuth><Layout /></RequireAuth>,
    errorElement: <RouteErrorFallback />,
    hydrateFallbackElement: <HydrateFallbackElement />,
    children: [
      { index: true, element: <Dashboard /> },
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'demos', element: <DemoLibrary /> },
      { path: 'workbench', element: <Workbench /> },
      { path: 'transaction', element: <Transaction /> },
      { path: 'entities', element: <Entities /> },
      { path: 'p2p', element: <RequireFeature feature="p2p.intel"><P2PIntelligence /></RequireFeature> },
      { path: 'analytics', element: <Analytics /> },
      { path: 'data-sources', element: <RequireFeature feature="scrapers"><DataSources /></RequireFeature> },
      { path: 'telegram', element: <RequireFeature feature="telegram.intel"><TelegramIntelligence /></RequireFeature> },
      { path: 'instagram', element: <RequireFeature feature="instagram.osint"><InstagramOSINT /></RequireFeature> },
      { path: 'harvest-feed', element: <RequireFeature feature="mulehunter.feed"><HarvestFeed /></RequireFeature> },
      { path: 'instagram-network', element: <RequireFeature feature="instagram.osint"><InstagramNetwork /></RequireFeature> },
      { path: 'off-ramp', element: <RequireFeature feature="fiat.statementflow"><FiatOffRamp /></RequireFeature> },
      { path: 'syndicates', element: <RequireFeature feature="mulehunter.syndicates"><SyndicateOperations /></RequireFeature> },
      // Fusion Canvas sandbox (synthetic) — sidebar Fusion; real cases use /cases/:id/canvas
      { path: 'fusion', element: <RequireFeature feature="investigation.auto"><FusionPage /></RequireFeature> },
      { path: 'search-history', element: <SearchHistory /> },
      { path: 'risk', element: <RiskRules /> },
      { path: 'cases', element: <Cases /> },
      { path: 'cases/:id', element: <CaseDetail /> },
      { path: 'cases/:id/macro', element: <RequireFeature feature="investigation.auto"><CaseMacro /></RequireFeature> },
      { path: 'cases/:id/canvas', element: <CaseCanvasPage /> },
      { path: 'cases/:id/report', lazy: async () => ({ Component: (await import('./pages/CaseReportPage')).default }) },
      { path: 'cases/:id/print', lazy: async () => ({ Component: (await import('./pages/CaseFullPrintPage')).default }) },
      { path: 'documentation', element: <Documentation /> },
      { path: 'feedback', element: <Feedback /> },
      { path: 'watchlist', element: <RequireFeature feature="watchlist"><WatchlistAlerts /></RequireFeature> },
      { path: 'settings', element: <Settings /> },
      { path: '*', element: <div className="p-8">Page not found</div> },
    ],
  },
]);

function clearCryptoSession() {
  try {
    localStorage.removeItem('token');
  } catch {
    /* ignore */
  }
}

function resolveCryptoMaintenanceStatusUrl(): string {
  const base = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(/\/$/, '');
  return `${base}/system/status`;
}

function App() {
  return (
    <MaintenanceGate
      statusUrl={resolveCryptoMaintenanceStatusUrl()}
      productName="CryptoFlow"
      accentColor="#0f766e"
      onClearSession={clearCryptoSession}
      loginHref="/login?expired=1"
    >
      <RouterProvider router={router} />
    </MaintenanceGate>
  );
}

export default App;
